import React, { useState } from 'react';
import ThumbIcon from 'shared/components/ui/ThumbIcon';
import { formatRelativeTime, formatDate } from 'features/agent-analytics/components/runCells';
import RunFeedbackControl from 'features/agent-analytics/components/RunFeedbackControl';
import { tableHeaderRowClass, tableHeaderCellClass, tableDataCellClass } from 'features/activity/styles/dataTableStyles';

const heading = 'text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]';

/** The feedback events on this run (spanId unset) or on one span of it, in the Scores tab. */
export function feedbackFor(feedback, spanId) {
  return (feedback || []).filter((fb) => (spanId ? fb.span_id === spanId : !fb.span_id));
}

/**
 * People's feedback, as the evals above it: a table of every event, newest
 * first, and a form to add one. With a span selected it is that span's
 * feedback, and a new one is saved on the span.
 */
export default function RunFeedbackPanel({ feedback, spanId, onSubmit }) {
  const [open, setOpen] = useState(false);
  const rows = feedbackFor(feedback, spanId);

  const submit = async (payload) => {
    await onSubmit({ ...payload, ...(spanId ? { span_id: spanId } : {}) });
    setOpen(false);
  };

  return (
    <section className="flex flex-col gap-2" aria-label="Feedback">
      <div className="flex items-center justify-between">
        <p className={heading}>Feedback</p>
        {!open && (
          <button type="button" onClick={() => setOpen(true)}
            className="rounded px-2 py-0.5 text-[11px] font-medium text-[color:var(--text-secondary)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]">
            Give feedback
          </button>
        )}
      </div>
      {open && <RunFeedbackControl onSubmit={submit} onCancel={() => setOpen(false)} />}
      <div className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface">
        <table className="w-full table-fixed text-left">
          <thead>
            <tr className={tableHeaderRowClass}>
              <th className={`${tableHeaderCellClass} w-[84px]`}>Rating</th>
              <th className={tableHeaderCellClass}>Comment</th>
              <th className={`${tableHeaderCellClass} w-[84px] text-right`}>When</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr className="h-8">
                <td colSpan={3} className={`${tableDataCellClass} text-[color:var(--text-muted)]`}>No feedback yet.</td>
              </tr>
            ) : rows.map((fb) => (
              <tr key={fb.id} className="border-b border-[color:var(--border-subtle)] last:border-b-0" data-testid="feedback-row">
                <td className={`${tableDataCellClass} py-2 align-top`}>
                  <span className="inline-flex items-center gap-1.5">
                    {fb.satisfaction === 'positive' && <ThumbIcon direction="up" filled size={13} />}
                    {fb.satisfaction === 'negative' && <ThumbIcon direction="down" filled size={13} />}
                    {fb.rating != null && <span className="tabular-nums text-[color:var(--text-secondary)]">{Number(fb.rating)}</span>}
                    {fb.satisfaction == null && fb.rating == null && <span className="text-[color:var(--text-muted)]">—</span>}
                  </span>
                </td>
                <td className={`${tableDataCellClass} max-w-0 whitespace-pre-wrap break-words py-2 align-top`} title={fb.distinct_id ? `From ${fb.distinct_id}` : undefined}>
                  {fb.comment || <span className="text-[color:var(--text-muted)]">—</span>}
                </td>
                <td className={`${tableDataCellClass} py-2 text-right align-top text-[11px] tabular-nums text-[color:var(--text-muted)]`} title={formatDate(fb.timestamp)}>
                  {formatRelativeTime(fb.timestamp)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
